'use client';
import React, { Component } from 'react';
import styled from 'styled-components';

import { NewRelic } from '../hooks/useNewRelic';
import localstorageService from '../LocalstorageService';
import { CriticalApplicationError } from '@/typings/errors';

interface ErrorState {
  hasError: boolean;
}
interface ErrorProps {
  children: React.ReactNode;
}

const clearAllCookies = function () {
  const cookies = document.cookie.split('; ');
  for (let c = 0; c < cookies.length; c++) {
    const d = window.location.hostname.split('.');
    while (d.length > 0) {
      const cookieBase =
        encodeURIComponent(cookies[c].split(';')[0].split('=')[0]) +
        '=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=' +
        d.join('.') +
        ' ;path=';
      const p = window.location.pathname.split('/');
      document.cookie = cookieBase + '/';
      while (p.length > 0) {
        document.cookie = cookieBase + p.join('/');
        p.pop();
      }
      d.shift();
    }
  }
};

class ErrorBoundary extends Component<ErrorProps, ErrorState> {
  constructor(props: ErrorProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      hasError: true,
    });

    // Create a CriticalApplicationError with the caught error as inner error
    const criticalError = new CriticalApplicationError(
      `Application render error: ${error.message}`,
      'client',
      error,
    );

    if (process.env.REACT_APP_ENV !== 'development') {
      // Client-side error reporting
      NewRelic()?.noticeError(criticalError);
    }

    // Log additional context for debugging
    // eslint-disable-next-line no-console
    console.error('Critical application error:', criticalError);
    // eslint-disable-next-line no-console
    console.error('Component stack:', errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <Container>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              width: '100%',
              backgroundColor: '#F9F3EB',
            }}>
            <h2 style={{ width: '100%', textAlign: 'center', margin: '20px' }}>
              Oops, there is an error!
            </h2>
            <h3 style={{ width: '100%', textAlign: 'center', margin: '20px' }}>
              We have informed our developers, try again.
            </h3>
            <span style={{ width: '100%', textAlign: 'center' }}>
              If this persists, please have your teacher contact support
            </span>

            <button
              onClick={() => {
                window.location.reload();

                //Clear all local storage to start completely fresh
                localstorageService.clear();
                try {
                  clearAllCookies();
                } catch (e) {}
              }}
              style={{
                margin: '20px',
                border: 'none',
                backgroundColor: '#33A0DD',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '24px',
                padding: '20px',
                color: 'white',
              }}>
              Try again
            </button>
          </div>
        </Container>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;

const StyledButton = styled.button`
  padding: 20px;
`;

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  flex-direction: column;
`;
